import type {OrderCountData, PerformanceData} from '../../types';

import {getColorToken} from '@ds/utils';
import Chart from 'packages/Chart/Chart';
import {useMemo} from 'react';

type DashboardChartProps =
  | {variant: 'performance'; data: PerformanceData}
  | {variant: 'orders'; data: OrderCountData};

const DAYS_OF_WEEK = ['ی', 'د', 'س', 'چ', 'پ', 'ج', 'ش'];

const DashboardChart: React.FC<DashboardChartProps> = ({variant, data}) => {
  const chartData = useMemo(() => {
    if (variant === 'performance') {
      return {
        labels:
          data.orders?.map((item) => DAYS_OF_WEEK[new Date(item.order_interval ?? '').getDay()]) ??
          [],
        firstDataset: data.orders?.map((item) => item.order_count ?? 0) ?? [],
        secondDataset: data.scores?.map((item) => item.total_score ?? 0) ?? []
      };
    }

    return {
      labels:
        data.order_count_per_day?.current?.map(
          (item) => DAYS_OF_WEEK[new Date(item.order_interval ?? '').getDay()]
        ) ?? [],
      firstDataset: data.order_count_per_day?.current?.map((item) => item.order_count ?? 0) ?? [],
      secondDataset: data.order_count_per_day?.old?.map((item) => item.order_count ?? 0) ?? []
    };
  }, [data, variant]);

  const isPerformance = variant === 'performance';
  const config = {
    type: 'line',
    data: {
      labels: chartData.labels,
      datasets: [
        {
          label: isPerformance ? 'تعداد سفارش‌ها' : 'هفته جاری',
          data: chartData.firstDataset,
          borderColor: getColorToken('--color-orange'),
          pointRadius: 0,
          yAxisID: 'y'
        },
        {
          label: isPerformance ? 'امتیاز عملکرد شما' : 'هفته قبل',
          data: chartData.secondDataset,
          borderColor: getColorToken('--color-gray-08'),
          borderDash: isPerformance ? undefined : [9, 9],
          pointRadius: 0,
          yAxisID: isPerformance ? 'y1' : 'y'
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          grid: {display: !isPerformance},
          ticks: {
            stepSize: 1,
            callback: (value: number) => (Number.isInteger(value) ? value : null)
          },
          min: 0,
          position: isPerformance ? 'right' : 'left'
        },
        ...(isPerformance
          ? {
              y1: {
                ticks: {
                  stepSize: 1,
                  callback: (value: number) => (Number.isInteger(value) ? value : null)
                },
                min: 0,
                position: 'left'
              }
            }
          : {}),
        x: {grid: {display: false}, min: 0}
      },
      plugins: {
        legend: {
          labels: {usePointStyle: true, boxHeight: 6, boxWidth: 6},
          position: 'bottom',
          align: 'end'
        },
        filler: {propagate: true}
      }
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-1">
        <h3 className="text-body-sm-emphasis text-neutral-fg">
          {isPerformance ? 'نمودار روزانه امتیاز و تعداد سفارش' : 'نمودار روزانه تعداد سفارش‌ها'}
        </h3>
        <p className="text-body-sm text-gray-07">
          {isPerformance
            ? 'تاثیر امتیاز عملکرد بر تعداد سفارش‌ها در ۷ روز گذشته'
            : 'تعداد سفارش‌های شما در ۷ روز گذشته'}
        </p>
      </header>
      <div className="h-70">
        <Chart config={config} />
      </div>
    </section>
  );
};

export default DashboardChart;
